import { useState, useEffect, useCallback } from 'react'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Select from '@cloudscape-design/components/select'
import Alert from '@cloudscape-design/components/alert'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Container from '@cloudscape-design/components/container'
import Header from '@cloudscape-design/components/header'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import { useAppStore } from '@/store/app-store'
import { applyBedrockRegion, getVendorFromPreset } from '@/utils/vendor-filter'
import {
  startScheduler,
  stopScheduler,
  getSchedulerState,
  isSchedulerRunning,
} from '@/engine/scheduler-singleton'
import { buildCloudHeaders } from '@/engine/api-client'
import { API_BASE } from '@/config/api'

// ── Interval options ──────────────────────────────────────────────────

const INTERVAL_OPTIONS = [
  { label: '5 minutes', value: String(5 * 60 * 1000) },
  { label: '15 minutes', value: String(15 * 60 * 1000) },
  { label: '30 minutes', value: String(30 * 60 * 1000) },
  { label: '1 hour', value: String(60 * 60 * 1000) },
  { label: '2 hours', value: String(2 * 60 * 60 * 1000) },
]

// ── Cloud scheduler status shape ──────────────────────────────────────

interface CloudSchedulerStatus {
  running: boolean
  continuous: boolean
  intervalMs: number
  profiles: { id: string; name: string; modelId: string; type: string }[]
  lastRunTime: string | null
  nextRunTime: string | null
  totalRuns: number
  currentlyRunning: boolean
}

export function SchedulerSection() {
  const executionMode = useAppStore((s) => s.executionMode)
  const allProfiles = useAppStore((s) => s.profiles)
  const allPresets = useAppStore((s) => s.presets)
  const bedrockRegion = useAppStore((s) => s.bedrockRegion)
  const isLocalMode = executionMode === 'local'

  const enabledProfiles = [
    ...allProfiles.filter((p) => p.enabled),
    ...allPresets
      .filter((p) => p.enabled)
      .map((p) => getVendorFromPreset(p) === 'bedrock' ? applyBedrockRegion(p, bedrockRegion) : p),
  ]

  // Interval selection
  const [selectedInterval, setSelectedInterval] = useState(INTERVAL_OPTIONS[2]) // default 30m

  // Local scheduler state (polled from singleton)
  const [localRunning, setLocalRunning] = useState(false)
  const [localTotalRuns, setLocalTotalRuns] = useState(0)
  const [localLastRunTime, setLocalLastRunTime] = useState<string | null>(null)
  const [localCurrentlyRunning, setLocalCurrentlyRunning] = useState(false)

  // Cloud scheduler state
  const [cloudStatus, setCloudStatus] = useState<CloudSchedulerStatus | null>(null)

  // ── Poll local scheduler state ────────────────────────────────────

  const refreshLocalState = useCallback(() => {
    const state = getSchedulerState()
    setLocalRunning(isSchedulerRunning())
    setLocalTotalRuns(state.totalRuns)
    setLocalLastRunTime(state.lastRunTime)
    setLocalCurrentlyRunning(state.currentlyRunning)
  }, [])

  // ── Fetch cloud scheduler status ──────────────────────────────────

  const fetchCloudStatus = useCallback(async () => {
    try {
      const headers = await buildCloudHeaders()
      const res = await fetch(`${API_BASE}/scheduler/status`, { headers })
      if (res.ok) {
        const data: CloudSchedulerStatus = await res.json()
        setCloudStatus(data)
      }
    } catch {
      /* ignore - cloud status is best-effort */
    }
  }, [])

  // Poll state on mount and every 5 seconds
  useEffect(() => {
    if (isLocalMode) {
      refreshLocalState()
      const id = setInterval(refreshLocalState, 5000)
      return () => clearInterval(id)
    } else {
      fetchCloudStatus()
      const id = setInterval(fetchCloudStatus, 15000)
      return () => clearInterval(id)
    }
  }, [isLocalMode, refreshLocalState, fetchCloudStatus])

  // ── Local mode actions ────────────────────────────────────────────

  const handleLocalStart = () => {
    const intervalMs = Number(selectedInterval.value)
    startScheduler(intervalMs)
    refreshLocalState()
  }

  const handleLocalStop = () => {
    stopScheduler()
    refreshLocalState()
  }

  // ── Cloud mode actions ────────────────────────────────────────────

  const handleCloudStartScheduled = async () => {
    try {
      const headers = await buildCloudHeaders()
      const intervalMs = Number(selectedInterval.value)
      await fetch(`${API_BASE}/scheduler/start`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          intervalMs,
          profiles: enabledProfiles,
          continuous: false,
        }),
      })
      fetchCloudStatus()
    } catch (err) {
      console.error('[SchedulerSection] Failed to start cloud scheduler:', err)
    }
  }

  const handleCloudStartContinuous = async () => {
    try {
      const headers = await buildCloudHeaders()
      await fetch(`${API_BASE}/scheduler/start`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          profiles: enabledProfiles,
          continuous: true,
        }),
      })
      fetchCloudStatus()
    } catch (err) {
      console.error('[SchedulerSection] Failed to start continuous scheduler:', err)
    }
  }

  const handleCloudStop = async () => {
    try {
      const headers = await buildCloudHeaders()
      await fetch(`${API_BASE}/scheduler/stop`, { method: 'POST', headers })
      fetchCloudStatus()
    } catch (err) {
      console.error('[SchedulerSection] Failed to stop cloud scheduler:', err)
    }
  }

  // ── Derived state ─────────────────────────────────────────────────

  const running = isLocalMode ? localRunning : cloudStatus?.running ?? false
  const totalRuns = isLocalMode ? localTotalRuns : cloudStatus?.totalRuns ?? 0
  const lastRunTime = isLocalMode ? localLastRunTime : cloudStatus?.lastRunTime ?? null
  const currentlyRunning = isLocalMode
    ? localCurrentlyRunning
    : cloudStatus?.currentlyRunning ?? false

  // Compute next run time for display
  const nextRunTimeDisplay = (() => {
    if (!running) return null
    if (!isLocalMode && cloudStatus?.nextRunTime) {
      return new Date(cloudStatus.nextRunTime).toLocaleTimeString()
    }
    if (!isLocalMode && cloudStatus?.continuous) {
      return 'Back-to-back'
    }
    if (isLocalMode && lastRunTime) {
      const intervalMs = Number(selectedInterval.value)
      const next = new Date(new Date(lastRunTime).getTime() + intervalMs)
      return `~${next.toLocaleTimeString()}`
    }
    if (isLocalMode) {
      const intervalMs = Number(selectedInterval.value)
      return `~${Math.round(intervalMs / 60000)} min`
    }
    return null
  })()

  // ── Render ────────────────────────────────────────────────────────

  return (
    <SpaceBetween size="l">
      <Box variant="p" color="text-body-secondary">
        Run benchmarks automatically at a set interval. Results accumulate over
        time in the Results page.
      </Box>

      {/* Local mode warning */}
      {isLocalMode && (
        <Alert type="warning">
          Local scheduler runs only while this browser tab is open. Closing the
          tab stops the scheduler.
        </Alert>
      )}

      {/* Status + Controls */}
      <Container
        header={
          <Header
            variant="h2"
            description={
              currentlyRunning ? 'A benchmark iteration is in progress…' : undefined
            }
          >
            <SpaceBetween direction="horizontal" size="xs">
              <span>Scheduler</span>
              {running ? (
                <StatusIndicator type="success">
                  {!isLocalMode && cloudStatus?.continuous
                    ? 'Continuous'
                    : 'Running'}
                </StatusIndicator>
              ) : (
                <StatusIndicator type="stopped">Stopped</StatusIndicator>
              )}
            </SpaceBetween>
          </Header>
        }
      >
        <SpaceBetween size="l">
          {/* Interval picker */}
          <div>
            <Box variant="awsui-key-label">Interval</Box>
            <Select
              selectedOption={selectedInterval}
              onChange={({ detail }) =>
                setSelectedInterval(
                  detail.selectedOption as typeof selectedInterval
                )
              }
              options={INTERVAL_OPTIONS}
              disabled={running}
            />
          </div>

          {/* Model list */}
          <div>
            <Box variant="awsui-key-label">
              {running
                ? 'Models currently being tested'
                : 'Models that will be tested'}
            </Box>
            {!isLocalMode && cloudStatus?.running && cloudStatus.profiles?.length > 0 ? (
              <SpaceBetween direction="horizontal" size="xs">
                {cloudStatus.profiles.map((p) => (
                  <Box key={p.id} display="inline-block" variant="code">
                    {p.name}{' '}
                    <Box display="inline" color="text-body-secondary">
                      ({p.type})
                    </Box>
                  </Box>
                ))}
              </SpaceBetween>
            ) : enabledProfiles.length > 0 ? (
              <SpaceBetween direction="horizontal" size="xs">
                {enabledProfiles.map((p) => (
                  <Box key={p.id} display="inline-block" variant="code">
                    {p.name}{' '}
                    <Box display="inline" color="text-body-secondary">
                      ({'type' in p ? p.type : 'preset'})
                    </Box>
                  </Box>
                ))}
              </SpaceBetween>
            ) : (
              <Box color="text-status-error" variant="small">
                No profiles enabled. Go to the Profiles tab and enable the
                models you want to test.
              </Box>
            )}
          </div>

          {/* Stats row */}
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Runs completed</Box>
              <Box variant="awsui-value-large">{totalRuns}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Last run</Box>
              <Box>
                {lastRunTime
                  ? new Date(lastRunTime).toLocaleTimeString()
                  : '-'}
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Next scheduled run</Box>
              <Box>{running && nextRunTimeDisplay ? nextRunTimeDisplay : '-'}</Box>
            </div>
          </ColumnLayout>

          {/* Action buttons */}
          <SpaceBetween direction="horizontal" size="xs">
            {isLocalMode ? (
              // Local mode: single Start/Stop button
              !localRunning ? (
                <Button
                  variant="primary"
                  onClick={handleLocalStart}
                  disabled={enabledProfiles.length === 0}
                >
                  Start Scheduled
                </Button>
              ) : (
                <Button variant="normal" onClick={handleLocalStop}>
                  Stop
                </Button>
              )
            ) : // Cloud mode: Start Scheduled + Continuous, or Stop
            !cloudStatus?.running ? (
              <>
                <Button
                  variant="primary"
                  onClick={handleCloudStartScheduled}
                  disabled={enabledProfiles.length === 0}
                >
                  Start Scheduled
                </Button>
                <Button
                  variant="normal"
                  onClick={handleCloudStartContinuous}
                  disabled={enabledProfiles.length === 0}
                >
                  Continuous
                </Button>
              </>
            ) : (
              <Button variant="normal" onClick={handleCloudStop}>
                Stop
              </Button>
            )}
          </SpaceBetween>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  )
}
